import { useState, useEffect, useRef } from 'react';
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Upload, X } from 'lucide-react';
import { useToast } from "@/components/ui/use-toast";
import { Label } from "@/components/ui/label";
import { useBuilder } from '@/contexts/BuilderContext';
import { ImageIcon } from 'lucide-react';

interface CompanyInfoSettingsProps {
  chatbotId: string;
  companyName?: string | null;
  companyLogo?: string | null;
  reportUnsavedChanges?: (isDirty: boolean) => void;
}

export const CompanyInfoSettings = ({
  chatbotId,
  companyName = "",
  companyLogo,
  reportUnsavedChanges
}: CompanyInfoSettingsProps) => {
  const [name, setName] = useState(companyName || "");
  const [isHovering, setIsHovering] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const previewUrlRef = useRef<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  
  const { updateBasicInfo, setPendingCompanyLogo, setRemoveCompanyLogo, state } = useBuilder();
  const { toast } = useToast();

  useEffect(() => {
    setName(companyName || "");
  }, [companyName]);

  // Create preview URL when pending file changes
  useEffect(() => {
    // If logo is marked for removal, clear preview immediately
    if (state.removeCompanyLogoOnSave) {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
      return;
    }

    if (state.pendingCompanyLogoFile) {
      // Create preview URL for the new file
      const url = URL.createObjectURL(state.pendingCompanyLogoFile);
      setPreviewUrl(url);
      previewUrlRef.current = url;
      
      // Cleanup function to revoke the URL when component unmounts or file changes
      return () => {
        if (previewUrlRef.current) {
          URL.revokeObjectURL(previewUrlRef.current);
          previewUrlRef.current = null;
        }
      };
    } else {
      // Clear preview if no pending file
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
        previewUrlRef.current = null;
      }
      setPreviewUrl(null);
    }
  }, [state.pendingCompanyLogoFile, state.removeCompanyLogoOnSave]);

  // Cleanup preview URL on unmount
  useEffect(() => {
    return () => {
      if (previewUrlRef.current) {
        URL.revokeObjectURL(previewUrlRef.current);
      }
    };
  }, []);

  // Handle company name change - only update local state
  const handleNameChange = (newName: string) => {
    setName(newName);
    // Update through context (will be saved via main Save Changes button)
    updateBasicInfo({ company_name: newName || null });
    if (reportUnsavedChanges) {
      reportUnsavedChanges(true);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    if (!file) return;
    
    // Check file type (company logo can be SVG too)
    if (!file.type.match(/image\/(jpeg|png|gif|webp|svg\+xml)/)) {
      toast({
        variant: "destructive",
        title: "Invalid file type",
        description: "Please upload an image file (JPEG, PNG, GIF, WEBP, SVG)",
      });
      return;
    }
    
    // Check file size (2MB for company logo)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        variant: "destructive",
        title: "File too large",
        description: "File size should be less than 2MB",
      });
      return;
    }
    
    // Store file in context for later upload (will be uploaded via main Save Changes button)
    setPendingCompanyLogo(file);
    setRemoveCompanyLogo(false);
    
    // Mark as dirty
    if (reportUnsavedChanges) {
      reportUnsavedChanges(true);
    }
    
    toast({
      title: "Company logo selected",
      description: "Save changes to upload the company logo.",
    });
  };
  
  const handleDeleteLogo = () => {
    // Clear preview immediately (synchronous)
    if (previewUrlRef.current) {
      URL.revokeObjectURL(previewUrlRef.current);
      previewUrlRef.current = null;
    }
    setPreviewUrl(null);
    
    // Mark logo for removal (will be removed via main Save Changes button)
    setPendingCompanyLogo(null);
    setRemoveCompanyLogo(true);
    
    // Update through context
    updateBasicInfo({ company_logo: null });
    
    // Mark as dirty
    if (reportUnsavedChanges) {
      reportUnsavedChanges(true);
    }
    
    toast({
      title: "Company logo marked for removal",
      description: "Save changes to remove the company logo.",
    });
  };
  
  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(part => part[0])
      .join('')
      .toUpperCase()
      .substring(0, 2);
  };

  return (
    <div className="flex flex-col space-y-4 py-4">
      <div className="flex flex-col space-y-2">
        <Label htmlFor="company-name">Company Name</Label>
        <Input
          id="company-name"
          placeholder="Enter company name"
          value={name}
          onChange={(e) => handleNameChange(e.target.value)}
        />
        <p className="text-sm text-gray-500">
          This name will be displayed in your chat widget
        </p>
      </div>
      <div className="flex flex-col space-y-2">
        <Label>Company Logo</Label>
        <div className="flex items-center space-x-4">
          <div 
            className="relative cursor-pointer"
            onMouseEnter={() => setIsHovering(true)}
            onMouseLeave={() => setIsHovering(false)}
          >
            <label htmlFor="company-logo-upload" className="cursor-pointer block">
              <Avatar 
                className="h-16 w-16"
                key={`${state.removeCompanyLogoOnSave}-${!!previewUrl}-${companyLogo || 'no-logo'}`}
              >
                {(() => {
                  // Priority 1: If logo is marked for removal, ALWAYS show fallback (highest priority)
                  if (state.removeCompanyLogoOnSave) {
                    return <AvatarFallback >
                      <ImageIcon className={'text-gray-400'} size={24} />
                    </AvatarFallback>;
                  }
                  // Priority 2: If there's a pending file and preview URL exists, show preview
                  if (state.pendingCompanyLogoFile && previewUrl) {
                    return <AvatarImage src={previewUrl} alt={name}/>;
                  }
                  // Priority 3: If there's a current logo URL and no pending file, show it
                  if (companyLogo && !state.pendingCompanyLogoFile) {
                    return <AvatarImage src={companyLogo} alt={name} />;
                  }
                  // Priority 4: Default to fallback with initials
                  return <AvatarFallback>
                    <ImageIcon className={'text-gray-400'} size={24} />
                  </AvatarFallback>;
                })()}
              </Avatar>

              {isHovering && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/50 rounded-full">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="text-white"
                    disabled={isUploading}
                    onClick={(e) => {
                      e.preventDefault();
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      }
                    }}
                  >
                    {isUploading ? (
                      <span className="animate-spin">⏳</span>
                    ) : (
                      <Upload size={24} />
                    )}
                  </Button>
                  {/* Show delete button only if there's something to delete (current logo or preview) and not already marked for removal */}
                  {(() => {
                    const hasLogoToDelete = !state.removeCompanyLogoOnSave && (companyLogo || previewUrl);
                    return hasLogoToDelete && !isUploading;
                  })() && (
                    <Button
                      variant="ghost"
                      size="icon"
                      className="text-white absolute bottom-0 right-0"
                      onClick={(e) => {
                        e.stopPropagation();
                        e.preventDefault();
                        handleDeleteLogo();
                      }}
                    >
                      <X size={16} />
                    </Button>
                  )}
                </div>
              )}
            </label>
            <input
              ref={fileInputRef}
              id="company-logo-upload"
              type="file"
              accept="image/jpeg,image/png,image/gif,image/webp"
              className="hidden"
              onChange={handleFileChange}
              disabled={isUploading}
            />
          </div>
          
          <div className="flex-1">
            <p className="text-sm text-gray-500">
              Upload a logo for your company. This will be displayed in the chat widget.
            </p>
          </div>
        </div>
      </div>
      
      {state.pendingCompanyLogoFile && (
        <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
          <p className="text-sm text-blue-800">
            Company logo file selected. Save changes to upload.
          </p>
        </div>
      )}
      
      {state.removeCompanyLogoOnSave && (
        <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
          <p className="text-sm text-yellow-800">
            Company logo marked for removal. Save changes to apply.
          </p>
        </div>
      )}
    </div>
  );
};
